import React, { useEffect, useState } from "react";
import {
  Box, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Typography, TextField, Grid, Button,
  Autocomplete, Switch, FormControlLabel, IconButton
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import api from "../services/api";
import { openAuthPdf } from "../services/openAuthPdf";
import { todayISO } from "../utils/date";

const todayStr = () => todayISO();
const daysAgoStr = (n) => new Date(Date.now() - n * 86400000).toISOString().split("T")[0];

/* ===== MAIN COMPONENT ===== */
export default function PurchaseOrders() {
  const [editId, setEditId] = useState(null);
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ from: daysAgoStr(90), to: todayStr(), name: "" });

  const loadDropdowns = async () => {
    try {
      const [accRes, prodRes] = await Promise.all([
        api.get("/purchaseOrders/accounts"),
        api.get("/purchaseOrders/products")
      ]);
      setAccounts(Array.isArray(accRes.data) ? accRes.data : []);
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);
    } catch (err) {
      console.error("Dropdown load error:", err);
    }
  };

  const loadPurchaseOrders = async (f = filters) => {
    try {
      const res = await api.get("/purchaseOrders", {
        params: { from: f.from || undefined, to: f.to || undefined, name: f.name || undefined },
      });
      if (!Array.isArray(res.data)) return;
      setRows(
        res.data.map(r => ({
          Master_ID: r.Master_ID,
          Customer_ID: r.Customer_ID,
          Name: r.Name,
          Date: r.Date ? r.Date.split("T")[0] : "",
          Details: [{
            Item: r.Item_Description || "",
            SO: r.SO || "",
            Qty: r.Qty || 0
          }],
          Active: r.Active
        }))
      );
    } catch (err) {
      console.error("Failed to load purchase orders:", err);
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadPurchaseOrders();
    // Load once on mount; loaders are stable for this view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <PurchaseOrderEditor
        editId={editId}
        setEditId={setEditId}
        reload={loadPurchaseOrders}
        accounts={accounts}
        products={products}
      />
      <PurchaseOrderList
        rows={rows}
        onEdit={setEditId}
        reload={loadPurchaseOrders}
        filters={filters}
        setFilters={setFilters}
        accounts={accounts}
      />
    </Box>
  );
}

/* ===== Filter bar ===== */
function OrderFilterBar({ filters, setFilters, onSearch, accounts }) {
  const names = (accounts || []).map((a) => a.SubAccount_Name).filter(Boolean);
  return (
    <Paper sx={{ p: 2, mb: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <Autocomplete
            freeSolo
            options={names}
            inputValue={filters.name}
            onInputChange={(e, val) => setFilters({ ...filters, name: val })}
            onChange={(e, val) => { const nf = { ...filters, name: val || "" }; setFilters(nf); onSearch(nf); }}
            renderInput={(params) => (
              <TextField {...params} label="Vendor (type to search)" fullWidth
                onKeyDown={(e) => { if (e.key === "Enter") onSearch({ ...filters }); }} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth
            value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth
            value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <Button variant="contained" fullWidth onClick={() => onSearch()}>Search</Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ===== PURCHASE ORDER EDITOR ===== */
function PurchaseOrderEditor({ editId, setEditId, reload, accounts, products }) {
  const [master, setMaster] = useState({
    Master_ID: "",
    CustomerObj: null,
    Date: "",
    Active: true,
    Items: [
      { ItemObj: null, SO: "", Qty: 0 }
    ]
  });

  useEffect(() => {
    if (!editId) {
      setMaster({
        Master_ID: "",
        CustomerObj: null,
        Date: "",
        Active: true,
        Items: [{ ItemObj: null, SO: "", Qty: 0 }]
      });
      return;
    }

    const loadEditData = async () => {
      try {
        const res = await api.get(`/purchaseOrders/${editId}`);
        const data = res.data;
        const items = data.details.map(d => ({
          ItemObj: products.find(p => p.Item_ID === d.Item_ID) || null,
          SO: d.SO,
          Qty: d.Qty
        }));

        setMaster({
          Master_ID: data.master.Master_ID,
          CustomerObj: accounts.find(a => a.id === data.master.Customer_ID) || null,
          Date: data.master.Date ? data.master.Date.split("T")[0] : "",
          Active: data.master.Active !== undefined ? data.master.Active : true,
          Items: items.length ? items : [{ ItemObj: null, SO: "", Qty: 0 }]
        });
      } catch (err) {
        console.error("Load edit data error:", err);
      }
    };

    loadEditData();
  }, [editId, accounts, products]);

  const resetForm = () => {
    setMaster({
      Master_ID: "",
      CustomerObj: null,
      Date: "",
      Active: true,
      Items: [{ ItemObj: null, SO: "", Qty: 0 }]
    });
    setEditId(null);
  };

  const handleItemChange = (index, key, value) => {
    const newItems = [...master.Items];
    newItems[index][key] = value;
    setMaster({ ...master, Items: newItems });
  };

  const addItem = () => {
    setMaster({ ...master, Items: [...master.Items, { ItemObj: null, SO: "", Qty: 0 }] });
  };

  const removeItem = index => {
    const newItems = [...master.Items];
    newItems.splice(index, 1);
    setMaster({ ...master, Items: newItems.length ? newItems : [{ ItemObj: null, SO: "", Qty: 0 }] });
  };

  const saveOrder = async () => {
    if (!master.CustomerObj) {
      alert("Please select a customer");
      return;
    }
    if (!master.Items.every(i => i.ItemObj)) {
      alert("Please select all items");
      return;
    }

    const payload = {
      master: {
        Master_ID: master.Master_ID,
        Customer_ID: master.CustomerObj.id,
        Name: master.CustomerObj.SubAccount_Name,
        Date: master.Date || null,
        Active: master.Active
      },
      details: master.Items.map(i => ({
        Item_ID: i.ItemObj.Item_ID,
        Item_Description: i.ItemObj.Product_Description,
        SO: i.SO,
        Qty: Number(i.Qty) || 0
      }))
    };

    try {
      const wasEdit = !!editId;
      if (wasEdit) {
        await api.put(`/purchaseOrders/${editId}`, payload);
      } else {
        await api.post("/purchaseOrders", payload);
      }
      resetForm();
      await reload();
      alert(wasEdit ? "Purchase Order updated" : "Purchase Order saved");
    } catch (err) {
      console.error("Save order error:", err.response?.data || err.message);
      alert("Error saving purchase order: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Box p={2}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">{editId ? "Edit Purchase Order" : "Add Purchase Order"}</Typography>

        <Grid container spacing={2} mt={1}>
          {/* Date */}
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={master.Date}
              onChange={e => setMaster({ ...master, Date: e.target.value })}
            />
          </Grid>

          {/* Customer */}
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            <Autocomplete
              options={accounts}
              getOptionLabel={o => o?.SubAccount_Name || ""}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={master.CustomerObj}
              onChange={(e, v) => setMaster({ ...master, CustomerObj: v })}
              renderInput={params => <TextField {...params} label="Customer" fullWidth />}
              fullWidth
              sx={{ width: "100%", minWidth: { xs: "100%", sm: 280 } }}
            />
          </Grid>

          {/* Active */}
          <Grid size={{ xs: 12, sm: 12, md: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={master.Active}
                  onChange={e => setMaster({ ...master, Active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        {/* Items */}
        <Box mt={2}>
          <Typography variant="subtitle1">Items</Typography>
          {master.Items.map((item, index) => (
            <Grid container spacing={2} key={index} alignItems="center" mt={1}>
              <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                <Autocomplete
                  options={products}
                  getOptionLabel={o => o?.Product_Description || ""}
                  isOptionEqualToValue={(opt, val) => opt?.Item_ID === val?.Item_ID}
                  value={item.ItemObj}
                  onChange={(e, v) => handleItemChange(index, "ItemObj", v)}
                  renderInput={params => <TextField {...params} label="Item" fullWidth />}
                  fullWidth
                  sx={{ width: "100%", minWidth: { xs: "100%", sm: 220 } }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                <TextField
                  label="Customer PO #"
                  fullWidth
                  value={item.SO}
                  onChange={e => handleItemChange(index, "SO", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 2, md: 2 }}>
                <TextField
                  type="number"
                  label="Qty"
                  fullWidth
                  value={item.Qty}
                  onChange={e => handleItemChange(index, "Qty", Number(e.target.value || 0))}
                />
              </Grid>
              <Grid size={{ xs: 2, sm: 1, md: 1 }}>
                <IconButton color="error" onClick={() => removeItem(index)}>
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Button startIcon={<Add />} sx={{ mt: 1 }} onClick={addItem}>
            Add Item
          </Button>
        </Box>

        <Box mt={2}>
          <Button variant="contained" onClick={saveOrder}>{editId ? "Update" : "Save"}</Button>
          {editId && <Button sx={{ ml: 2 }} onClick={resetForm}>Cancel</Button>}
        </Box>
      </Paper>
    </Box>
  );
}

/* ===== PURCHASE ORDER LIST ===== */
function PurchaseOrderList({ rows, onEdit, reload, filters, setFilters, accounts }) {
  const del = async id => {
    if (!window.confirm("Delete this purchase order?")) return;
    try {
      await api.delete(`/purchaseOrders/${id}`);
      reload();
    } catch (err) {
      console.error("Delete error:", err.response?.data || err.message);
      alert("Error deleting purchase order: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h6" sx={{ mb: 1 }}>Purchase Orders</Typography>
      <OrderFilterBar filters={filters} setFilters={setFilters} onSearch={reload} accounts={accounts} />
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Customer</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Items</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.Master_ID}>
              <TableCell>{r.Name}</TableCell>
              <TableCell>{r.Date}</TableCell>
              <TableCell>{r.Active ? "Yes" : "No"}</TableCell>
              <TableCell>
                {r.Details.map((d, idx) => (
                  <div key={idx}>
                    {d.Item} | {d.SO} | Qty: {d.Qty}
                  </div>
                ))}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Button size="small" color="success" onClick={() => openAuthPdf(`/purchaseOrders/${r.Master_ID}/pdf`)}>View</Button>
                <Button size="small" onClick={() => onEdit(r.Master_ID)}>Edit</Button>
                <Button size="small" color="error" onClick={() => del(r.Master_ID)}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
    </Box>
  );
}
